import { useState } from 'react';
import { Tooltip } from '@mui/material';
import {
  IAssistantMessage,
  IAssistantPolicyField,
  IAssistantProposalOp,
  IPolicy,
  PolicyType,
  langauge,
  narrowProposalOp,
  policyColumnDescriptions,
} from 'shared';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

import { Button } from '../../../common/Atoms/Button';
import { Pill } from '../../../common/Atoms/Pill';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { useGroups } from '../../hooks/useGroups';
import { usePolicies } from '../../hooks/usePolicies';

interface ProposalCardProps {
  message: IAssistantMessage;
  onApply: () => Promise<void>;
  onDismiss: () => Promise<void>;
  busy: boolean;
  /** Past chats cannot be continued, so their proposals can only be viewed */
  /** When set, apply/dismiss are hidden and this explains why */
  readOnly?: string;
}

const opColors: Record<IAssistantProposalOp[`op`], `green` | `blue` | `red` | `orange`> = {
  create: `green`,
  update: `blue`,
  delete: `red`,
  turn_safe_mode_on: `orange`,
};

const opLabels: Partial<Record<IAssistantProposalOp[`op`], string>> = {
  turn_safe_mode_on: `Safe mode`,
};

type FieldValue = IAssistantPolicyField[`value`] | undefined;

interface ChangeRow {
  key: string;
  label: string;
  description?: string;
  current?: string;
  proposed: string;
}

/** Column name fallback when the policy type (and so its labels) is unknown */
function humanise(field: string) {
  const text = field.replace(/_/g, ` `);

  return text.charAt(0).toUpperCase() + text.slice(1);
}

function ProposalOp({ op, rows }: { op: IAssistantProposalOp; rows: ChangeRow[] }) {
  // Deletes start expanded so the real target is visible before approval, whatever the summary says
  const [expanded, setExpanded] = useState(op.op === `delete`);

  return (
    <li className={ `py-2` }>
      <button
        type={ `button` }
        className={ `flex w-full items-start gap-2 text-left` }
        onClick={ () => setExpanded(current => !current) }
        disabled={ !rows.length }
      >
        <Pill
          message={ opLabels[op.op] ?? op.op }
          color={ opColors[op.op] }
          className={ `mt-0.5 shrink-0 capitalize` }
        />
        <span className={ `flex-1 text-sm text-gray-700` }>{ op.summary }</span>
        {
          rows.length > 0 && (
            expanded
              ? <ChevronDownIcon className={ `mt-1 h-4 w-4 shrink-0 text-gray-400` } />
              : <ChevronRightIcon className={ `mt-1 h-4 w-4 shrink-0 text-gray-400` } />
          )
        }
      </button>
      {
        expanded && rows.length > 0 &&
        <dl className={ `mt-2 ml-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-l-2 border-slate-200 pl-3 text-xs` }>
          {
            rows.map(row => (
              <div
                key={ row.key }
                className={ `contents` }
              >
                <Tooltip
                  title={ row.description ?? `` }
                  placement={ `left` }
                >
                  <dt className={ `text-gray-500` }>{ row.label }</dt>
                </Tooltip>
                <dd className={ `flex flex-wrap items-center gap-1 break-all text-gray-800` }>
                  {
                    row.current !== undefined &&
                    <>
                      <span className={ `text-gray-400 line-through` }>{ row.current }</span>
                      <ArrowRightIcon className={ `h-3 w-3 shrink-0 text-gray-400` } />
                    </>
                  }
                  <span>{ row.proposed }</span>
                </dd>
              </div>
            ))
          }
        </dl>
      }
    </li>
  );
}

/** Renders the assistant's proposed policy changes with Apply all / Dismiss */
export function ProposalCard({ message, onApply, onDismiss, busy, readOnly }: ProposalCardProps) {
  const { data: policies } = usePolicies();
  const { data: templates } = useEmailTemplates();
  const { data: groups } = useGroups();
  const proposal = message.proposal;

  if (!proposal) {
    return null;
  }

  function formatValue(field: string, value: FieldValue): string {
    if (value === null || value === undefined || value === ``) {
      return `(not set)`;
    }

    if (field === `template_id`) {
      return templates?.find(template => template.id === value)?.name ?? String(value);
    }

    return String(value);
  }

  function groupNames(groupIds: number[]): string {
    return groupIds.map(id => groups?.find(group => group.id === id)?.title ?? `Group ${id}`).join(`, `);
  }

  function rowsFor(op: IAssistantProposalOp): ChangeRow[] {
    if (op.op === `turn_safe_mode_on`) {
      return [];
    }

    const existing = op.op === `create`
      ? undefined
      : policies?.find(item => item.policy.id === op.policyId);

    // op.summary is model prose: identify update/delete targets from the policy id instead
    const targetRows: ChangeRow[] = op.op === `create`
      ? []
      : [{
        key: `target`,
        label: `Policy`,
        proposed: existing ? `${existing.policy.title || `Untitled`} (#${op.policyId})` : `#${op.policyId}${policies ? ` (no longer exists)` : ``}`,
      }];

    if (op.op === `delete`) {
      if (!existing) {
        return targetRows;
      }

      return [
        ...targetRows,
        { key: `type`, label: `Type`, proposed: existing.policy.policy_type ? langauge.policy.labels[existing.policy.policy_type].short.singular : `(not set)` },
        { key: `state`, label: `State`, proposed: langauge.policy.state[existing.policy.state].label },
        { key: `groups`, label: `Groups`, proposed: groupNames(existing.groupIds) || `(none)` },
        { key: `impact`, label: `Impact`, proposed: `This policy is deleted and stops applying to these groups.` },
      ];
    }

    const policyType: PolicyType | undefined = op.op === `create` ? op.policyType : existing?.policy.policy_type;
    const columns = policyType ? policyColumnDescriptions[policyType] : undefined;

    const rows: ChangeRow[] = op.policy.map(pair => {
      const column = columns?.[pair.field as keyof IPolicy];
      const currentValue = existing?.policy[pair.field as keyof IPolicy] as FieldValue;

      return {
        key: pair.field,
        label: column?.label ?? humanise(pair.field),
        description: column?.description,
        current: existing ? formatValue(pair.field, currentValue) : undefined,
        proposed: formatValue(pair.field, pair.value),
      };
    });

    if (op.groupIds) {
      rows.push({
        key: `groupIds`,
        label: `Groups`,
        description: `Customer groups this policy applies to.`,
        current: existing ? groupNames(existing.groupIds) : undefined,
        proposed: groupNames(op.groupIds),
      });
    }

    return [...targetRows, ...rows];
  }

  const status = message.proposalStatus;

  return (
    <div className={ `w-full rounded-xl border border-slate-200 bg-white p-4 shadow-xs` }>
      <Paragraph className={ `mb-1` }>{ proposal.title }</Paragraph>
      <ul className={ `divide-y divide-slate-100` }>
        {
          proposal.ops.map(narrowProposalOp).map((op, index) => (
            <ProposalOp
              key={ index }
              op={ op }
              rows={ rowsFor(op) }
            />
          ))
        }
      </ul>
      <div className={ `mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3` }>
        {
          status === `pending` && readOnly &&
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { readOnly }
          </Paragraph>
        }
        {
          status === `pending` && !readOnly &&
          <>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { `Nothing changes until you apply.` }
            </Paragraph>
            <div className={ `flex gap-2` }>
              <Button
                size={ `sm` }
                color={ `gray` }
                disabled={ busy }
                onClick={ onDismiss }
              >
                { `Dismiss` }
              </Button>
              <Button
                size={ `sm` }
                disabled={ busy }
                loading={ busy }
                onClick={ onApply }
              >
                { `Apply all` }
              </Button>
            </div>
          </>
        }
        {
          status === `applied` &&
          <Paragraph
            size={ `xs` }
            className={ `flex items-center gap-1 !text-green-700` }
          >
            <CheckCircleIcon className={ `h-4 w-4` } />
            { `Applied` }
          </Paragraph>
        }
        {
          status === `failed` &&
          <Paragraph
            size={ `xs` }
            variant={ `error` }
          >
            { `Could not apply: ${message.applyError ?? `unknown error`}` }
          </Paragraph>
        }
        {
          status === `dismissed` &&
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { `Dismissed` }
          </Paragraph>
        }
      </div>
    </div>
  );
}
